import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import PublishRoundedIcon from '@mui/icons-material/PublishRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import UnpublishedRoundedIcon from '@mui/icons-material/UnpublishedRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Drawer,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { EmptyState, ErrorState, LoadingState, PageContainer } from '@/shared/ui';
import { useUploadAsset, useMediaAssets } from '@/shared/media';
import type { MediaOwnerType } from '@/shared/media';

import { useContentPermissions } from '../../lib/useContentPermissions';
import {
  useContentPage,
  useContentPages,
  useCreateContentPage,
  useDeleteContentPage,
  usePublishContentPage,
  useReplaceContentBlocks,
  useUnpublishContentPage,
  useUpdateContentPage,
} from '../../hooks';
import {
  useContentSettings,
  useUpdateContentSettings,
  useDomainPermissions,
  useFeedback,
  useSettingsForm,
  toSettingsUpdate,
  SettingsCard,
  SwitchField,
  TextInputField,
  FeedbackSnackbar,
} from '@/features/store-settings';
import { CONTENT_PAGE_TEMPLATES, getContentPageTemplate, templateDraft } from '../../model/contentTemplates';
import type {
  BodyFormat,
  ContentBlock,
  ContentBlockItem,
  ContentBlockType,
  ContentPage,
  ContentPageType,
  ContentStatus,
  UpdatePageRequest,
} from '../../model/types';

// ── Constants ────────────────────────────────────────────────────────────────

/** Monospace font stack — JetBrains Mono is the OBZR design system monospace token. */
const MONOSPACE_FONT_FAMILY = 'JetBrains Mono, SFMono-Regular, Consolas, Liberation Mono, monospace';

const PAGE_TYPE_OPTIONS: Array<{ value: ContentPageType | 'all'; label: string }> = [
  { value: 'all', label: 'All types' },
  { value: 'SHOP_INFO_SECTION', label: 'Shop info' },
  { value: 'LEGAL_DOCUMENT', label: 'Legal' },
  { value: 'GENERIC_PAGE', label: 'Generic' },
];

const PAGE_STATUS_OPTIONS: Array<{ value: ContentStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'ARCHIVED', label: 'Archived' },
];

const BODY_FORMAT_OPTIONS: Array<{ value: BodyFormat; label: string }> = [
  { value: 'MARKDOWN', label: 'Markdown' },
  { value: 'PLAINTEXT', label: 'Plain text' },
];

const BLOCK_TYPE_OPTIONS: Array<{ value: ContentBlockType; label: string }> = [
  { value: 'PAYMENT_METHODS', label: 'Payment methods' },
  { value: 'DELIVERY_OPTIONS', label: 'Delivery options' },
  { value: 'RETURN_SECTIONS', label: 'Return sections' },
  { value: 'FAQ', label: 'FAQ' },
  { value: 'CONTACT_ROWS', label: 'Contact rows' },
];

const PAGE_TEMPLATE_OPTIONS = CONTENT_PAGE_TEMPLATES.map((template) => ({
  value: template.key,
  label: template.label,
  helperText: template.helperText,
}));

const LOCALE_OPTIONS = ['ru', 'en'];

const MEDIA_OWNER_TYPE_OPTIONS: Array<{ value: MediaOwnerType; label: string }> = [
  { value: 'CMS_PAGE', label: 'CMS page' },
  { value: 'LEGAL_DOCUMENT', label: 'Legal document' },
  { value: 'BANNER', label: 'Banner' },
];

// ── Pure utilities ───────────────────────────────────────────────────────────

function getErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred.';
}

const emptyBlockItem = (): ContentBlockItem => ({ primary: '', secondary: '' });

function emptyBlock(blockType: ContentBlockType = 'FAQ'): ContentBlock {
  return {
    blockType,
    position: 0,
    items: [emptyBlockItem()],
  };
}

function clonePage(page: ContentPage): ContentPage {
  return {
    ...page,
    blocks: (page.blocks ?? []).map((block) => ({
      ...block,
      items: (block.items ?? []).map((item) => ({ ...item })),
    })),
  };
}

function formatBlocks(blocks: ContentBlock[] | null | undefined): string {
  const value = blocks ?? [];
  return JSON.stringify(value, null, 2);
}

/**
 * Parse and structurally validate a blocks JSON string.
 * Each block must have a string `blockType` and an array `items`.
 */
function parseBlocks(value: string): ContentBlock[] {
  if (!value.trim()) {
    return [];
  }
  const parsed: unknown = JSON.parse(value);
  if (!Array.isArray(parsed)) {
    throw new Error('Blocks JSON must be an array.');
  }
  for (let i = 0; i < parsed.length; i++) {
    const block = parsed[i] as Record<string, unknown>;
    if (typeof block?.blockType !== 'string') {
      throw new Error(`Block[${i}]: blockType must be a string.`);
    }
    if (!Array.isArray(block?.items)) {
      throw new Error(`Block[${i}] "${block.blockType}": items must be an array.`);
    }
  }
  return parsed as ContentBlock[];
}

function normalizeBlock(block: ContentBlock): ContentBlock {
  return {
    blockType: block.blockType,
    position: block.position ?? 0,
    items: block.items?.length ? block.items : [emptyBlockItem()],
  };
}

function sortBlocks(blocks: ContentBlock[]): ContentBlock[] {
  return [...blocks]
    .map((block, index) => ({ block, index }))
    .sort((left, right) => {
      const leftPosition = left.block.position ?? 0;
      const rightPosition = right.block.position ?? 0;
      return leftPosition - rightPosition || left.index - right.index;
    })
    .map(({ block }) => block);
}

function cloneBlocks(blocks: ContentBlock[]): ContentBlock[] {
  return blocks.map((block) => ({
    ...block,
    items: block.items.map((item) => ({ ...item })),
  }));
}

function serializeBlocks(blocks: ContentBlock[]): string {
  return formatBlocks(sortBlocks(blocks));
}

function statusColor(status: ContentStatus): 'default' | 'primary' | 'success' | 'warning' {
  switch (status) {
    case 'PUBLISHED':
      return 'success';
    case 'DRAFT':
      return 'warning';
    default:
      return 'default';
  }
}

function typeLabel(type: ContentPageType): string {
  return PAGE_TYPE_OPTIONS.find((option) => option.value === type)?.label ?? type;
}

function statusLabel(status: ContentStatus): string {
  return PAGE_STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status;
}

function defaultMediaOwnerType(pageType: ContentPageType): MediaOwnerType {
  return pageType === 'LEGAL_DOCUMENT' ? 'LEGAL_DOCUMENT' : 'CMS_PAGE';
}

// ── Draft model ──────────────────────────────────────────────────────────────

interface PageDraftState {
  slug: string;
  type: ContentPageType;
  locale: string;
  title: string;
  subtitle: string;
  body: string;
  bodyFormat: BodyFormat;
  blocksJson: string;
  version: number | null;
}

function draftFromPage(page: ContentPage): PageDraftState {
  return {
    slug: page.slug,
    type: page.type,
    locale: page.locale,
    title: page.title ?? '',
    subtitle: page.subtitle ?? '',
    body: page.body ?? '',
    bodyFormat: page.bodyFormat,
    blocksJson: formatBlocks(page.blocks),
    version: page.version ?? null,
  };
}

function createDraftFromDefaults(): PageDraftState {
  return {
    slug: '',
    type: 'GENERIC_PAGE',
    locale: 'ru',
    title: '',
    subtitle: '',
    body: '',
    bodyFormat: 'MARKDOWN',
    blocksJson: formatBlocks([]),
    version: null,
  };
}

function draftFromTemplate(templateKey: string): PageDraftState | null {
  const template = getContentPageTemplate(templateKey);
  return template
    ? {
        ...templateDraft(template),
        version: null,
      }
    : null;
}

// ── Sub-components ───────────────────────────────────────────────────────────

function PageEditorSkeleton() {
  return (
    <Stack spacing={2.5}>
      <LoadingState label="Loading content page…" />
    </Stack>
  );
}

function PageFieldGroup({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              aria-hidden
              sx={{
                display: 'grid',
                placeItems: 'center',
                width: 36,
                height: 36,
                borderRadius: 2,
                // H1 fix: use design-system token instead of hardcoded '#fff'
                color: 'common.white',
                background: (theme) =>
                  `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.dark})`,
              }}
            >
              <ArticleRoundedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography variant="h6">{title}</Typography>
          </Stack>
          <Divider />
          {children}
        </Stack>
      </CardContent>
    </Card>
  );
}

function BlocksJsonHint() {
  return (
    <Alert severity="info" variant="outlined">
      Blocks are edited in a structured form and mirrored as JSON for the service contract. Each
      block must match the schema: blockType, position, and an items array with primary/secondary
      fields.
    </Alert>
  );
}

function MarkdownPreview({
  body,
  format,
  emptyLabel,
}: {
  body: string;
  format: BodyFormat;
  emptyLabel: string;
}) {
  if (!body.trim()) {
    return <Alert severity="info">{emptyLabel}</Alert>;
  }

  if (format === 'PLAINTEXT') {
    return (
      <Typography
        component="pre"
        sx={{
          m: 0,
          p: 2,
          borderRadius: 2,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.default',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          // L5 fix: use shared constant instead of inline string literal
          fontFamily: MONOSPACE_FONT_FAMILY,
          fontSize: '0.875rem',
          lineHeight: 1.6,
        }}
      >
        {body}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.default',
        '& > :first-of-type': { mt: 0 },
        '& > :last-child': { mb: 0 },
        '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 0, mb: 1, lineHeight: 1.2 },
        '& p': { mt: 0, mb: 1 },
        '& ul, & ol': { mt: 0, mb: 1, pl: 3 },
        '& li + li': { mt: 0.5 },
        '& blockquote': {
          m: 0,
          pl: 1.5,
          py: 0.5,
          borderLeft: '3px solid',
          borderColor: 'divider',
          color: 'text.secondary',
        },
        '& code': {
          px: 0.5,
          py: 0.125,
          borderRadius: 0.75,
          bgcolor: 'action.hover',
          // L5 fix: use shared constant
          fontFamily: MONOSPACE_FONT_FAMILY,
        },
        '& pre': {
          p: 1.5,
          borderRadius: 1.5,
          overflow: 'auto',
          bgcolor: 'action.hover',
        },
        '& a': {
          color: 'primary.main',
          wordBreak: 'break-word',
        },
        '& img': {
          maxWidth: '100%',
          borderRadius: 1.5,
        },
      }}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
    </Box>
  );
}

function ContentMediaPanel({
  page,
  draftBodyFormat,
  disabled,
  onInsert,
}: {
  page: ContentPage | undefined;
  draftBodyFormat: BodyFormat;
  disabled: boolean;
  onInsert: (value: string) => void;
}) {
  // C1 fix: import from @/shared/media instead of @/features/catalog/hooks
  const upload = useUploadAsset();
  const assetsQuery = useMediaAssets(
    {
      ownerType: page ? defaultMediaOwnerType(page.type) : 'CMS_PAGE',
      ownerId: page?.slug,
      visibility: 'PUBLIC',
    },
    { enabled: Boolean(page) },
  );
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [ownerType, setOwnerType] = useState<MediaOwnerType>(page ? defaultMediaOwnerType(page.type) : 'CMS_PAGE');

  useEffect(() => {
    setOwnerType(page ? defaultMediaOwnerType(page.type) : 'CMS_PAGE');
  }, [page?.slug, page?.type]);

  const handleUpload = async (file: File | undefined) => {
    if (!file || !page) {
      return;
    }
    upload.mutate(
      { file, ownerType, ownerId: page.slug, visibility: 'PUBLIC' },
      {
        onSuccess: (asset) => {
          setUploadError(null);
          void assetsQuery.refetch();
          const snippet =
            draftBodyFormat === 'MARKDOWN'
              ? `![${file.name}](${asset.publicUrl})`
              : asset.publicUrl;
          onInsert(`\n\n${snippet}`);
        },
        onError: (error) => {
          setUploadError(error instanceof Error ? error.message : 'Upload failed.');
        },
      },
    );
  };

  if (!page) {
    return null;
  }

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="h6">Media</Typography>
          <Typography variant="body2" color="text.secondary">
            Upload a page asset or reuse an existing one from this page's gallery.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              select
              size="small"
              label="Owner type"
              value={ownerType}
              onChange={(event) => setOwnerType(event.target.value as MediaOwnerType)}
              disabled={disabled}
              sx={{ minWidth: 220 }}
            >
              {MEDIA_OWNER_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined"
              startIcon={<AddRoundedIcon />}
              onClick={() => inputRef.current?.click()}
              disabled={disabled || upload.isPending}
            >
              Upload media
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(event) => {
                void handleUpload(event.target.files?.[0]);
                event.target.value = '';
              }}
            />
          </Stack>
          {uploadError ? <Alert severity="error">{uploadError}</Alert> : null}
          {upload.isPending ? (
            <Typography variant="body2" color="text.secondary">
              Uploading...
            </Typography>
          ) : null}

          <Divider />

          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Typography variant="subtitle2">Page gallery</Typography>
              <Typography variant="caption" color="text.secondary">
                {assetsQuery.data?.length ?? 0} asset{(assetsQuery.data?.length ?? 0) === 1 ? '' : 's'}
              </Typography>
            </Stack>
            {assetsQuery.isLoading ? (
              <Typography variant="body2" color="text.secondary">
                Loading page assets...
              </Typography>
            ) : assetsQuery.isError ? (
              <Alert severity="warning">Unable to load existing page assets.</Alert>
            ) : (assetsQuery.data ?? []).length === 0 ? (
              <Alert severity="info">No assets uploaded for this page yet.</Alert>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                }}
              >
                {(assetsQuery.data ?? []).map((asset) => (
                  <Card key={asset.assetId} variant="outlined" sx={{ overflow: 'hidden' }}>
                    <Box
                      sx={{
                        aspectRatio: '16 / 9',
                        backgroundColor: 'action.hover',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        component="img"
                        src={asset.publicUrl}
                        alt={asset.fileName ?? asset.publicAssetId}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                    </Box>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack spacing={1}>
                        <Box>
                          <Typography variant="body2" fontWeight={700} noWrap>
                            {asset.fileName ?? asset.publicAssetId}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {asset.publicAssetId}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => {
                              const snippet =
                                draftBodyFormat === 'MARKDOWN'
                                  ? `![${asset.fileName ?? asset.publicAssetId}](${asset.publicUrl})`
                                  : asset.publicUrl;
                              onInsert(`\n\n${snippet}`);
                            }}
                            disabled={disabled}
                          >
                            Insert
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            // L4 fix: catch clipboard permission errors silently
                            onClick={() => void navigator.clipboard?.writeText(asset.publicUrl).catch(() => undefined)}
                          >
                            Copy URL
                          </Button>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

function ContentBlocksEditor({
  value,
  disabled,
  onChange,
}: {
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const parsed = useMemo(() => {
    try {
      return { blocks: sortBlocks(parseBlocks(value).map(normalizeBlock)), error: null as string | null };
    } catch (error) {
      return { blocks: [] as ContentBlock[], error: getErrorMessage(error) };
    }
  }, [value]);

  const updateBlocks = (next: ContentBlock[]) => onChange(serializeBlocks(next));

  const updateBlock = (index: number, updater: (block: ContentBlock) => ContentBlock) => {
    const next = cloneBlocks(parsed.blocks).map((block, currentIndex) => (currentIndex === index ? updater(block) : block));
    updateBlocks(next);
  };

  const addBlock = () => {
    updateBlocks([...cloneBlocks(parsed.blocks), { ...emptyBlock(), position: parsed.blocks.length }]);
  };

  const removeBlock = (index: number) => {
    updateBlocks(
      parsed.blocks
        .filter((_, currentIndex) => currentIndex !== index)
        .map((block, currentIndex) => ({ ...block, position: currentIndex })),
    );
  };

  const moveBlock = (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= parsed.blocks.length) {
      return;
    }
    const next = cloneBlocks(parsed.blocks);
    const [moved] = next.splice(index, 1);
    next.splice(targetIndex, 0, moved);
    updateBlocks(next.map((block, currentIndex) => ({ ...block, position: currentIndex })));
  };

  const updateItem = (
    blockIndex: number,
    itemIndex: number,
    field: 'primary' | 'secondary',
    fieldValue: string,
  ) => {
    updateBlock(blockIndex, (block) => ({
      ...block,
      items: block.items.map((item, currentIndex) =>
        currentIndex === itemIndex
          ? {
              ...item,
              [field]: fieldValue,
            }
          : item,
      ),
    }));
  };

  const moveItem = (blockIndex: number, itemIndex: number, direction: -1 | 1) => {
    const targetIndex = itemIndex + direction;
    const block = parsed.blocks[blockIndex];
    if (!block || targetIndex < 0 || targetIndex >= block.items.length) {
      return;
    }
    const items = block.items.map((item) => ({ ...item }));
    const [moved] = items.splice(itemIndex, 1);
    items.splice(targetIndex, 0, moved);
    updateBlock(blockIndex, (current) => ({
      ...current,
      items,
    }));
  };

  const removeItem = (blockIndex: number, itemIndex: number) => {
    updateBlock(blockIndex, (current) => ({
      ...current,
      items: current.items.filter((_, currentIndex) => currentIndex !== itemIndex),
    }));
  };

  if (parsed.error) {
    return (
      <Stack spacing={1.5}>
        <Alert severity="warning">
          Blocks JSON could not be parsed. Fix the JSON below to keep the structured editor in sync.
          Details: {parsed.error}
        </Alert>
        <TextField
          label="Blocks JSON"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          fullWidth
          multiline
          minRows={16}
          // M4 fix: use MUI v7 slotProps instead of deprecated inputProps
          slotProps={{
            input: {
              style: {
                fontFamily: MONOSPACE_FONT_FAMILY,
                fontSize: '0.875rem',
              },
            },
          }}
        />
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <BlocksJsonHint />
      <Stack spacing={2}>
        {parsed.blocks.map((block, blockIndex) => (
          <Card key={`${block.blockType}-${blockIndex}`} variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  alignItems={{ sm: 'flex-start' }}
                  justifyContent="space-between"
                >
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ flex: 1 }}>
                    <TextField
                      select
                      label="Block type"
                      value={block.blockType}
                      onChange={(event) =>
                        updateBlock(blockIndex, (current) => ({
                          ...current,
                          blockType: event.target.value as ContentBlockType,
                        }))
                      }
                      disabled={disabled}
                      fullWidth
                    >
                      {BLOCK_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      label="Position"
                      type="number"
                      value={block.position ?? 0}
                      onChange={(event) =>
                        updateBlock(blockIndex, (current) => ({
                          ...current,
                          position: Number(event.target.value || 0),
                        }))
                      }
                      disabled={disabled}
                      sx={{ maxWidth: 160 }}
                    />
                  </Stack>
                  <Stack direction="row" spacing={0.5} sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}>
                    <IconButton
                      size="small"
                      onClick={() => moveBlock(blockIndex, -1)}
                      disabled={disabled || blockIndex === 0}
                      aria-label="Move block up"
                    >
                      <ArrowUpwardRoundedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => moveBlock(blockIndex, 1)}
                      disabled={disabled || blockIndex === parsed.blocks.length - 1}
                      aria-label="Move block down"
                    >
                      <ArrowDownwardRoundedIcon fontSize="small" />
                    </IconButton>
                    <Button
                      size="small"
                      variant="text"
                      color="error"
                      onClick={() => removeBlock(blockIndex)}
                      disabled={disabled}
                    >
                      Remove block
                    </Button>
                  </Stack>
                </Stack>
                <Stack spacing={1.5}>
                  {block.items.map((item, itemIndex) => (
                    <Box
                      key={`${block.blockType}-${blockIndex}-${itemIndex}`}
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Stack spacing={1.5}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                          <Typography variant="caption" color="text.secondary">
                            Item {itemIndex + 1}
                          </Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <IconButton
                              size="small"
                              onClick={() => moveItem(blockIndex, itemIndex, -1)}
                              disabled={disabled || itemIndex === 0}
                              aria-label="Move item up"
                            >
                              <ArrowUpwardRoundedIcon fontSize="inherit" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => moveItem(blockIndex, itemIndex, 1)}
                              disabled={disabled || itemIndex === block.items.length - 1}
                              aria-label="Move item down"
                            >
                              <ArrowDownwardRoundedIcon fontSize="inherit" />
                            </IconButton>
                            {block.items.length > 1 ? (
                              <Button
                                size="small"
                                variant="text"
                                color="error"
                                onClick={() => removeItem(blockIndex, itemIndex)}
                                disabled={disabled}
                              >
                                Remove item
                              </Button>
                            ) : null}
                          </Stack>
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                          <TextField
                            label="Primary"
                            value={item.primary}
                            onChange={(event) =>
                              updateItem(blockIndex, itemIndex, 'primary', event.target.value)
                            }
                            disabled={disabled}
                            fullWidth
                          />
                          <TextField
                            label="Secondary"
                            value={item.secondary ?? ''}
                            onChange={(event) =>
                              updateItem(blockIndex, itemIndex, 'secondary', event.target.value)
                            }
                            disabled={disabled}
                            fullWidth
                          />
                        </Stack>
                      </Stack>
                    </Box>
                  ))}
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      updateBlock(blockIndex, (current) => ({
                        ...current,
                        items: [...current.items, emptyBlockItem()],
                      }))
                    }
                    disabled={disabled}
                  >
                    Add item
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
      <Button
        variant="outlined"
        onClick={addBlock}
        disabled={disabled}
      >
        Add block
      </Button>
      <TextField
        label="Blocks JSON"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        fullWidth
        multiline
        minRows={12}
        // M4 fix: use MUI v7 slotProps instead of deprecated inputProps
        slotProps={{
          input: {
            style: {
              fontFamily: MONOSPACE_FONT_FAMILY,
              fontSize: '0.875rem',
            },
          },
        }}
      />
    </Stack>
  );
}

// ── Editor Drawer ────────────────────────────────────────────────────────────

const PAGE_EDITOR_DRAWER_WIDTH = {
  xs: '100vw',
  sm: '92vw',
  md: '88vw',
  lg: 'min(88vw, 1280px)',
};

// ── Content Settings Drawer ──────────────────────────────────────────────────

/** Narrower panel width — settings panels don't need the full-width editor canvas. */
const SETTINGS_DRAWER_WIDTH = {
  xs: '100vw',
  sm: 500,
  md: 540,
};

/**
 * Right-side settings panel for the Content domain, wired to
 * GET/PUT /api/v1/store/settings/content via the existing store-settings hooks.
 * Mirrors the visual language of ContentPageEditorDrawer (sticky header, scrollable
 * body, sticky footer with Save / Reset) using the store-settings SettingsCard /
 * SwitchField / TextInputField primitives so it stays consistent with every other
 * settings screen in the portal.
 */
function ContentSettingsDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { canView, canManage } = useDomainPermissions('content');
  const query = useContentSettings();
  const update = useUpdateContentSettings();
  const form = useSettingsForm(query.data);
  const { feedback, success, error: notifyError, clear } = useFeedback();

  const handleSave = () => {
    if (!form.draft) return;
    update.mutate(toSettingsUpdate(form.draft), {
      onSuccess: () => success('Content settings saved.'),
      onError: (err) => {
        notifyError(err.message);
        // 409 Conflict means the version is stale — reload from server so the next
        // save attempt carries the correct version.
        if (err.status === 409) void query.refetch();
      },
    });
  };

  const disabled = !canManage;
  const draft = form.draft;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        slotProps={{
          paper: {
            sx: {
              width: SETTINGS_DRAWER_WIDTH,
              maxWidth: '100vw',
              height: '100vh',
              display: 'flex',
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* ── Sticky header ── */}
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              pt: { xs: 2, sm: 3 },
              pb: 2,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              bgcolor: 'background.paper',
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <Stack spacing={0.75}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SettingsRoundedIcon fontSize="small" color="primary" />
                <Typography variant="h5">Content settings</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Storefront pages, legal content, banners and SEO metadata.
              </Typography>
            </Stack>
            <IconButton onClick={onClose} aria-label="Close content settings">
              <CloseRoundedIcon />
            </IconButton>
          </Box>

          {/* ── Scrollable body ── */}
          <Box sx={{ flex: 1, overflow: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 } }}>
            {query.isLoading ? (
              <LoadingState label="Loading content settings…" />
            ) : query.isError ? (
              <ErrorState
                title="Settings unavailable"
                message={query.error.message}
                onRetry={() => void query.refetch()}
              />
            ) : !canView ? (
              <ErrorState
                title="No access"
                message="You do not have permission to view content settings."
              />
            ) : draft ? (
              <Stack spacing={2.5}>
                <SettingsCard title="Content modules" icon={<ArticleRoundedIcon />}>
                  <SwitchField
                    label="Storefront pages"
                    description="Enable CMS-managed shop-info, legal and generic storefront pages."
                    checked={draft.storefrontPagesEnabled ?? false}
                    onChange={(value) => form.setField('storefrontPagesEnabled', value)}
                    disabled={disabled}
                  />
                  <SwitchField
                    label="Legal content pages"
                    description="Enable CMS editing and publishing of legal document pages (privacy policy, terms of service). Does not affect customer-facing display — use the Legal display settings below."
                    checked={draft.legalContentEnabled ?? false}
                    onChange={(value) => form.setField('legalContentEnabled', value)}
                    disabled={disabled}
                  />
                  <SwitchField
                    label="Banners"
                    description="Enable promotional and informational banners on the storefront."
                    checked={draft.bannersEnabled ?? false}
                    onChange={(value) => form.setField('bannersEnabled', value)}
                    disabled={disabled}
                  />
                  <SwitchField
                    label="Landing pages"
                    description="Enable standalone landing pages linked from campaigns."
                    checked={draft.landingPagesEnabled ?? false}
                    onChange={(value) => form.setField('landingPagesEnabled', value)}
                    disabled={disabled}
                  />
                  <TextInputField
                    label="Publishing rule"
                    description="Default publishing rule applied to new content pages."
                    value={draft.publishingRule ?? ''}
                    onChange={(value) => form.setField('publishingRule', value)}
                    disabled={disabled}
                  />
                </SettingsCard>

                <SettingsCard
                  title="Legal display"
                  icon={<GavelRoundedIcon />}
                  description="Controls what legal information is shown to customers in the Telegram Mini App shop-info page."
                >
                  <SwitchField
                    label="Legal section"
                    description="Show the legal section (accordion) in the shop-info page. Must be enabled for any legal content to be visible to customers."
                    checked={draft.legalSummaryBlockEnabled ?? false}
                    onChange={(value) => form.setField('legalSummaryBlockEnabled', value)}
                    disabled={disabled}
                  />
                  <SwitchField
                    label="Show legal document links"
                    description="Display privacy policy and legal document links inside the legal section. Requires 'Legal section' to be enabled."
                    checked={draft.legalDocsRequired ?? false}
                    onChange={(value) => form.setField('legalDocsRequired', value)}
                    disabled={disabled}
                  />
                </SettingsCard>

                <SettingsCard
                  title="SEO metadata defaults"
                  icon={<TravelExploreRoundedIcon />}
                  description="Fallback values used when a page does not set its own SEO fields."
                >
                  <TextInputField
                    label="Default title"
                    description="Fallback <title> tag when a page has no title configured."
                    value={draft.seoMetadataDefaults?.title ?? ''}
                    onChange={(value) =>
                      form.setField('seoMetadataDefaults', {
                        ...draft.seoMetadataDefaults,
                        title: value || null,
                      })
                    }
                    disabled={disabled}
                  />
                  <TextInputField
                    label="Default description"
                    description="Fallback <meta name=description> when a page has no description."
                    value={draft.seoMetadataDefaults?.description ?? ''}
                    onChange={(value) =>
                      form.setField('seoMetadataDefaults', {
                        ...draft.seoMetadataDefaults,
                        description: value || null,
                      })
                    }
                    disabled={disabled}
                    multiline
                    rows={2}
                  />
                </SettingsCard>
              </Stack>
            ) : null}
          </Box>

          {/* ── Sticky footer — Save / Reset (only when the user can manage) ── */}
          {canManage && draft ? (
            <Box
              sx={{
                px: { xs: 2, sm: 3 },
                py: 2,
                borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                bgcolor: 'background.paper',
                display: 'flex',
                gap: 1.5,
                justifyContent: 'flex-end',
              }}
            >
              <Button
                variant="outlined"
                onClick={form.reset}
                disabled={!form.dirty || update.isPending}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!form.dirty || update.isPending}
              >
                {update.isPending ? 'Saving…' : 'Save'}
              </Button>
            </Box>
          ) : null}
        </Box>
      </Drawer>

      <FeedbackSnackbar feedback={feedback} onClose={clear} />
    </>
  );
}

type ContentPageEditorMode = 'create' | 'edit';

function ContentPageEditorDrawer({
  open,
  mode,
  page,
  pageSlug,
  loading,
  error,
  draft,
  canManage,
  blocksError,
  operationError,
  onClose,
  onDraftChange,
  onApplyTemplate,
  onSave,
  onPublish,
  onUnpublish,
  onDelete,
  onReset,
  saving,
  publishing,
  unpublishing,
  deleting,
}: {
  open: boolean;
  mode: ContentPageEditorMode | null;
  page: ContentPage | undefined;
  pageSlug: string | undefined;
  loading: boolean;
  error: string | null;
  draft: PageDraftState;
  canManage: boolean;
  blocksError: string | null;
  /** Separate error slot for publish/unpublish/delete mutations (not block validation). */
  operationError: string | null;
  onClose: () => void;
  onDraftChange: (updater: (current: PageDraftState) => PageDraftState) => void;
  onApplyTemplate: (templateKey: string) => void;
  onSave: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
  onDelete: () => void;
  onReset: () => void;
  saving: boolean;
  publishing: boolean;
  unpublishing: boolean;
  deleting: boolean;
}) {
  const [templateKey, setTemplateKey] = useState('blank');
  // H2 fix: confirmation state for delete
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setTemplateKey('blank');
    }
  }, [mode, open]);

  const headerTitle = mode === 'create' ? 'New content page' : page?.title ?? page?.slug ?? 'Content page';
  const headerSubtitle =
    mode === 'create'
      ? 'Start from a blank page or apply a ready-made template, then save the draft.'
      : page
        ? `${page.slug} · ${page.locale} · ${typeLabel(page.type)}`
        : pageSlug
          ? `Loading ${pageSlug}...`
          : 'Open an existing page from the list.';

  const primaryLabel = mode === 'create' ? 'Create page' : 'Save draft';
  const body = (() => {
    if (mode === 'edit' && loading) {
      return <PageEditorSkeleton />;
    }

    if (mode === 'edit' && error) {
      return (
        <ErrorState
          title="Page unavailable"
          message={error}
        />
      );
    }

    return (
      <Stack spacing={2.5}>
        {page && page.publishedBody ? (
          <Alert severity="success" variant="outlined">
            Published snapshot exists. Draft changes stay private until you publish again.
          </Alert>
        ) : null}

        {/* L6 fix: operation-level errors (publish/unpublish/delete) shown near action buttons */}
        {operationError ? (
          <Alert severity="error">{operationError}</Alert>
        ) : null}

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ sm: 'center' }}
        >
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {mode === 'edit' && canManage ? (
              <Button
                variant="contained"
                startIcon={<PublishRoundedIcon />}
                onClick={() => void onPublish()}
                disabled={publishing}
              >
                Publish
              </Button>
            ) : null}
            {mode === 'edit' && canManage && page?.status === 'PUBLISHED' ? (
              <Button
                variant="outlined"
                startIcon={<UnpublishedRoundedIcon />}
                onClick={() => void onUnpublish()}
                disabled={unpublishing}
              >
                Unpublish
              </Button>
            ) : null}
            {canManage ? (
              <Button
                variant="outlined"
                onClick={onReset}
                disabled={saving || publishing || unpublishing || deleting}
              >
                Reset
              </Button>
            ) : null}
            {canManage ? (
              <Button
                variant="contained"
                onClick={() => void onSave()}
                disabled={saving || publishing || unpublishing || deleting || (!draft.slug.trim() && mode === 'create')}
              >
                {primaryLabel}
              </Button>
            ) : null}
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {mode === 'edit' && canManage ? (
              // H2 fix: open confirmation dialog before deleting
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineRoundedIcon />}
                onClick={() => setDeleteConfirmOpen(true)}
                disabled={deleting}
              >
                Delete
              </Button>
            ) : null}
          </Stack>
        </Stack>

        <PageFieldGroup title="Template">
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              Choose a starter template from the dropdown, then apply it to overwrite the current draft structure.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
              <TextField
                select
                label="Template"
                value={templateKey}
                onChange={(event) => setTemplateKey(event.target.value)}
                disabled={!canManage}
                fullWidth
              >
                <MenuItem value="blank">Blank page</MenuItem>
                {PAGE_TEMPLATE_OPTIONS.map((template) => (
                  <MenuItem key={template.value} value={template.value}>
                    {template.label}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="outlined"
                onClick={() => onApplyTemplate(templateKey)}
                disabled={!canManage || templateKey === 'blank'}
                sx={{ minWidth: { xs: '100%', sm: 160 } }}
              >
                Apply template
              </Button>
            </Stack>
          </Stack>
        </PageFieldGroup>

        <PageFieldGroup title="Page details">
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label="Slug"
                value={draft.slug}
                onChange={(event) => onDraftChange((current) => ({ ...current, slug: event.target.value }))}
                disabled={mode === 'edit' || !canManage}
                fullWidth
                helperText={mode === 'edit' ? 'Slug is immutable after creation.' : 'The slug will be used in the page URL.'}
              />
              <TextField
                select
                label="Locale"
                value={draft.locale}
                onChange={(event) => onDraftChange((current) => ({ ...current, locale: event.target.value }))}
                disabled={!canManage}
                fullWidth
              >
                {LOCALE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label="Title"
                value={draft.title}
                onChange={(event) => onDraftChange((current) => ({ ...current, title: event.target.value }))}
                disabled={!canManage}
                fullWidth
              />
              <TextField
                select
                label="Page type"
                value={draft.type}
                onChange={(event) =>
                  onDraftChange((current) => ({ ...current, type: event.target.value as ContentPageType }))
                }
                disabled={!canManage}
                fullWidth
              >
                {PAGE_TYPE_OPTIONS.filter((option) => option.value !== 'all').map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <TextField
              label="Subtitle"
              value={draft.subtitle}
              onChange={(event) => onDraftChange((current) => ({ ...current, subtitle: event.target.value }))}
              disabled={!canManage}
              fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                select
                label="Body format"
                value={draft.bodyFormat}
                onChange={(event) =>
                  onDraftChange((current) => ({ ...current, bodyFormat: event.target.value as BodyFormat }))
                }
                disabled={!canManage}
                fullWidth
              >
                {BODY_FORMAT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField label="Version" value={draft.version ?? 0} disabled fullWidth />
            </Stack>
            <TextField
              label="Body"
              value={draft.body}
              onChange={(event) => onDraftChange((current) => ({ ...current, body: event.target.value }))}
              disabled={!canManage}
              fullWidth
              multiline
              minRows={10}
            />
          </Stack>
        </PageFieldGroup>

        <PageFieldGroup title="Body preview">
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              Rendered from the current draft using the selected body format.
            </Typography>
            <MarkdownPreview
              body={draft.body}
              format={draft.bodyFormat}
              emptyLabel="No draft body content yet."
            />
          </Stack>
        </PageFieldGroup>

        {mode === 'edit' ? (
          <ContentMediaPanel
            page={page}
            draftBodyFormat={draft.bodyFormat}
            disabled={!canManage}
            onInsert={(value) =>
              onDraftChange((current) => ({
                ...current,
                body: `${current.body}${value}`,
              }))
            }
          />
        ) : null}

        <PageFieldGroup title="Content blocks">
          <Stack spacing={2}>
            {blocksError ? <Alert severity="error">{blocksError}</Alert> : null}
            <ContentBlocksEditor
              value={draft.blocksJson}
              disabled={!canManage}
              onChange={(value) => onDraftChange((current) => ({ ...current, blocksJson: value }))}
            />
          </Stack>
        </PageFieldGroup>

        {page && (page.publishedBody || page.body) ? (
          <Card>
            <CardContent>
              <Typography variant="h6">Published snapshot</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                The live storefront still shows the last published body until you press Publish.
              </Typography>
              <MarkdownPreview
                body={page.publishedBody ?? page.body ?? ''}
                format={page.bodyFormat}
                emptyLabel="No published body snapshot yet."
              />
            </CardContent>
          </Card>
        ) : null}
      </Stack>
    );
  })();

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        slotProps={{
          paper: {
            sx: {
              width: PAGE_EDITOR_DRAWER_WIDTH,
              maxWidth: '100vw',
              height: '100vh',
              display: 'flex',
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              pt: { xs: 2, sm: 3 },
              pb: 2,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              bgcolor: 'background.paper',
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <Stack spacing={0.75} sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="h5">{headerTitle}</Typography>
                {page ? <Chip size="small" variant="outlined" label={`v${page.version ?? 0}`} /> : null}
                {page ? <Chip size="small" color={statusColor(page.status)} label={statusLabel(page.status)} /> : null}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {headerSubtitle}
              </Typography>
            </Stack>
            <IconButton onClick={onClose} aria-label="Close page editor">
              <CloseRoundedIcon />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 } }}>{body}</Box>
        </Box>
      </Drawer>

      {/* H2 fix: delete confirmation dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="delete-page-dialog-title"
      >
        <DialogTitle id="delete-page-dialog-title">Delete page?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete{' '}
            <strong>{page?.slug ?? pageSlug}</strong>.{' '}
            {page?.status === 'PUBLISHED' && (
              <>This page is currently <strong>published</strong> — deleting it will remove it from the live storefront immediately.</>
            )}
            {' '}This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleting}
            onClick={() => {
              setDeleteConfirmOpen(false);
              void onDelete();
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

/** Merchant CMS workspace: list pages, open a page, edit draft content and blocks, then publish. */
export function ContentPagesPage() {
  const { canView, canManage } = useContentPermissions();
  const navigate = useNavigate();
  const params = useParams<{ slug?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<ContentPageType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<ContentStatus | 'all'>('all');
  const [locale, setLocale] = useState(searchParams.get('locale') ?? 'ru');
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState<PageDraftState>(createDraftFromDefaults());
  const [blocksError, setBlocksError] = useState<string | null>(null);
  // L6 fix: separate error state for publish/unpublish/delete operations
  const [operationError, setOperationError] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [tablePage, setTablePage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  // M2 fix: track whether the user has unsaved edits to avoid overwriting them on background refetch
  const [isDirty, setIsDirty] = useState(false);
  // Settings gear: controls the Content domain settings drawer
  const [settingsOpen, setSettingsOpen] = useState(false);

  const listQuery = useContentPages(
    {
      type: typeFilter === 'all' ? undefined : typeFilter,
      status: statusFilter === 'all' ? undefined : statusFilter,
      locale,
    },
    canView,
  );
  const pages = useMemo(() => listQuery.data ?? [], [listQuery.data]);
  const filteredPages = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return pages;
    }
    return pages.filter(
      (page) =>
        page.slug.toLowerCase().includes(term) ||
        (page.title ?? '').toLowerCase().includes(term) ||
        typeLabel(page.type).toLowerCase().includes(term),
    );
  }, [pages, search]);
  const pagedPages = useMemo(
    () => filteredPages.slice(tablePage * rowsPerPage, tablePage * rowsPerPage + rowsPerPage),
    [filteredPages, rowsPerPage, tablePage],
  );

  const selectedSlug = params.slug;
  const pageQuery = useContentPage(selectedSlug, locale, canView && Boolean(selectedSlug));
  const selectedPage = pageQuery.data;
  const editorMode: ContentPageEditorMode | null = createOpen ? 'create' : selectedSlug ? 'edit' : null;
  const editorOpen = Boolean(editorMode);

  const createMutation = useCreateContentPage();
  const updateMutation = useUpdateContentPage();
  const deleteMutation = useDeleteContentPage();
  const publishMutation = usePublishContentPage();
  const unpublishMutation = useUnpublishContentPage();
  const replaceBlocksMutation = useReplaceContentBlocks();

  // M3 fix: merge locale into existing search params instead of replacing all params
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('locale', locale);
        return next;
      },
      { replace: true },
    );
  }, [locale, setSearchParams]);

  // M2 fix: reset dirty flag when navigating to a different page
  useEffect(() => {
    setIsDirty(false);
  }, [selectedSlug]);

  // M2 fix: only reseed draft from server if the user hasn't made unsaved edits
  useEffect(() => {
    if (isDirty) return;
    if (selectedPage) {
      setDraft(draftFromPage(clonePage(selectedPage)));
      setBlocksError(null);
      setCreateOpen(false);
    } else if (!selectedSlug) {
      setDraft(createDraftFromDefaults());
      setBlocksError(null);
    }
  }, [selectedPage, selectedSlug, isDirty]);

  useEffect(() => {
    setTablePage(0);
  }, [locale, search, statusFilter, typeFilter]);

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(filteredPages.length / rowsPerPage) - 1);
    if (tablePage > lastPage) {
      setTablePage(lastPage);
    }
  }, [filteredPages.length, rowsPerPage, tablePage]);

  // M2 fix: wrapper that marks draft as dirty before updating
  const handleDraftChange = (updater: (current: PageDraftState) => PageDraftState) => {
    setIsDirty(true);
    setDraft(updater);
  };

  const handleSelectPage = (slug: string) => {
    setCreateOpen(false);
    navigate(`/content/pages/${slug}?locale=${locale}`);
  };

  const openCreateDrawer = () => {
    setBlocksError(null);
    setOperationError(null);
    setDraft(createDraftFromDefaults());
    setIsDirty(false);
    setCreateOpen(true);
  };

  const closeEditor = () => {
    if (createOpen) {
      setCreateOpen(false);
      return;
    }
    if (selectedSlug) {
      navigate(`/content/pages?locale=${locale}`);
    }
  };

  /**
   * C2 fix: two-phase save with immediate version update after the first mutation.
   *
   * If the text-content mutation (updatePage/createPage) succeeds but the blocks
   * mutation fails, the server has already incremented the page version.  We update
   * draft.version immediately after the first mutation so that a retry does not hit
   * a 409 Conflict on the second attempt.
   */
  const persistDraft = async () => {
    const activeSlug = selectedSlug ?? draft.slug.trim();
    if (!activeSlug) {
      throw new Error('Select or create a page before saving.');
    }
    setBlocksError(null);
    const blocks = parseBlocks(draft.blocksJson);
    const updateBody: UpdatePageRequest = {
      title: draft.title || null,
      subtitle: draft.subtitle || null,
      // Use ?? null (not || null) so that an empty string '' is sent as-is, telling the backend
      // to clear the body. The || operator converts '' to null which the backend interprets as
      // "no change", causing an empty body to never be persisted (B1 fix).
      body: draft.body ?? null,
      bodyFormat: draft.bodyFormat,
      version: draft.version,
    };
    const nextPage =
      editorMode === 'create'
        ? await createMutation.mutateAsync({
            slug: activeSlug,
            type: draft.type,
            locale: draft.locale,
            title: updateBody.title,
            subtitle: updateBody.subtitle,
            body: updateBody.body,
            bodyFormat: updateBody.bodyFormat,
          })
        : await updateMutation.mutateAsync({
            slug: activeSlug,
            locale,
            body: updateBody,
          });

    // Immediately record the new server version so that a retry after a partial
    // blocks failure does not send the stale version and receive a 409 Conflict.
    setDraft((prev) => ({ ...prev, version: nextPage.version ?? prev.version }));

    await replaceBlocksMutation.mutateAsync({
      slug: nextPage.slug,
      locale: editorMode === 'create' ? draft.locale : locale,
      body: { blocks },
    });
    return nextPage;
  };

  const handleSaveDraft = async () => {
    try {
      const nextPage = await persistDraft();
      setDraft(draftFromPage(nextPage));
      setIsDirty(false);
      if (editorMode === 'create') {
        setCreateOpen(false);
        navigate(`/content/pages/${nextPage.slug}?locale=${nextPage.locale}`);
      }
    } catch (error) {
      setBlocksError(getErrorMessage(error));
    }
  };

  const handlePublish = async () => {
    if (!selectedSlug) return;
    setOperationError(null);
    try {
      await persistDraft();
      const nextPage = await publishMutation.mutateAsync({ slug: selectedSlug, locale });
      setDraft(draftFromPage(nextPage));
      setIsDirty(false);
    } catch (error) {
      // L6 fix: publish errors go to operationError, not blocksError
      setOperationError(getErrorMessage(error));
    }
  };

  const handleUnpublish = async () => {
    if (!selectedSlug) return;
    setOperationError(null);
    try {
      const nextPage = await unpublishMutation.mutateAsync({ slug: selectedSlug, locale });
      setDraft(draftFromPage(nextPage));
      setIsDirty(false);
    } catch (error) {
      // L6 fix: unpublish errors go to operationError, not blocksError
      setOperationError(getErrorMessage(error));
    }
  };

  const handleDelete = async () => {
    if (!selectedSlug) return;
    setOperationError(null);
    try {
      await deleteMutation.mutateAsync({ slug: selectedSlug, locale });
      navigate('/content/pages');
    } catch (error) {
      // L6 fix: delete errors go to operationError, not blocksError
      setOperationError(getErrorMessage(error));
    }
  };

  const handleResetDraft = () => {
    if (editorMode === 'create') {
      setDraft(createDraftFromDefaults());
      setBlocksError(null);
      setOperationError(null);
      setIsDirty(false);
      return;
    }
    if (selectedPage) {
      setDraft(draftFromPage(clonePage(selectedPage)));
      setBlocksError(null);
      setOperationError(null);
      setIsDirty(false);
    }
  };

  if (!canView) {
    return (
      <PageContainer title="Content" subtitle="Manage storefront pages, legal documents and blocks.">
        <ErrorState
          title="No access"
          message="You do not have permission to view merchant content."
        />
      </PageContainer>
    );
  }

  if (listQuery.isLoading) {
    return (
      <PageContainer title="Content" subtitle="Manage storefront pages, legal documents and blocks.">
        <LoadingState label="Loading content pages…" />
      </PageContainer>
    );
  }

  if (listQuery.isError) {
    return (
      <PageContainer title="Content" subtitle="Manage storefront pages, legal documents and blocks.">
        <ErrorState
          title="Content unavailable"
          message={listQuery.error.message}
          onRetry={() => void listQuery.refetch()}
        />
      </PageContainer>
    );
  }

  return (
      <PageContainer
      title="Content"
      subtitle="Manage storefront pages, legal documents and structured content blocks."
      actions={
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Content settings">
            <IconButton
              onClick={() => setSettingsOpen(true)}
              aria-label="Open content settings"
              size="small"
            >
              <SettingsRoundedIcon />
            </IconButton>
          </Tooltip>
          {canManage ? (
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={openCreateDrawer}
            >
              New page
            </Button>
          ) : null}
        </Stack>
      }
    >
      {!canManage ? (
        <Alert severity="info" variant="outlined" sx={{ mb: 2.5 }}>
          Read only: you can browse pages, but this tenant role cannot save or publish changes.
        </Alert>
      ) : null}

      <Stack spacing={2.5}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <TextField
            size="small"
            label="Search pages"
            placeholder="Slug, title or type"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ flex: 1 }}
            slotProps={{
              input: {
                startAdornment: <SearchRoundedIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
              },
            }}
          />
          <TextField
            select
            size="small"
            label="Locale"
            value={locale}
            onChange={(event) => setLocale(event.target.value)}
            sx={{ width: { xs: '100%', md: 150 } }}
          >
            {LOCALE_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Type"
            value={typeFilter}
            onChange={(event) => setTypeFilter(event.target.value as ContentPageType | 'all')}
            sx={{ width: { xs: '100%', md: 180 } }}
          >
            {PAGE_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as ContentStatus | 'all')}
            sx={{ width: { xs: '100%', md: 180 } }}
          >
            {PAGE_STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Box>
                  <Typography variant="h6">Pages</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {filteredPages.length} page{filteredPages.length === 1 ? '' : 's'} in {locale}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  {/* L3 fix: add aria-label for screen readers */}
                  <IconButton
                    onClick={(event) => setMenuAnchor(event.currentTarget)}
                    size="small"
                    aria-label="Page list options"
                  >
                    <MoreVertRoundedIcon />
                  </IconButton>
                  <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                    {/* H4 fix: actually trigger a refetch when Refresh is clicked */}
                    <MenuItem
                      onClick={() => {
                        setMenuAnchor(null);
                        void listQuery.refetch();
                      }}
                    >
                      Refresh
                    </MenuItem>
                  </Menu>
                </Stack>
              </Stack>

              {filteredPages.length === 0 ? (
                <EmptyState
                  title="No pages yet"
                  description="Create the first storefront page or loosen the filters."
                  action={
                    canManage ? (
                      <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreateDrawer}>
                        New page
                      </Button>
                    ) : undefined
                  }
                />
              ) : (
                <>
                  <TableContainer sx={{ maxHeight: 640 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Page</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Open</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pagedPages.map((page) => {
                          const selected = page.slug === selectedSlug;
                          return (
                            <TableRow
                              key={`${page.slug}:${page.locale}`}
                              hover
                              selected={selected}
                              sx={{ cursor: 'pointer' }}
                              onClick={() => handleSelectPage(page.slug)}
                            >
                              <TableCell>
                                <Stack spacing={0.25}>
                                  <Typography variant="body2" fontWeight={700}>
                                    {page.title ?? page.slug}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {page.slug} · {typeLabel(page.type)} · {page.locale}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Chip size="small" color={statusColor(page.status)} label={statusLabel(page.status)} />
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  component={RouterLink}
                                  to={`/content/pages/${page.slug}?locale=${locale}`}
                                  size="small"
                                  variant={selected ? 'contained' : 'outlined'}
                                  startIcon={<EditRoundedIcon />}
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  Open
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={filteredPages.length}
                    page={tablePage}
                    onPageChange={(_, nextPage) => setTablePage(nextPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(event) => {
                      setRowsPerPage(Number(event.target.value));
                      setTablePage(0);
                    }}
                    rowsPerPageOptions={[5, 8, 12, 20]}
                  />
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <ContentPageEditorDrawer
        open={editorOpen}
        mode={editorMode}
        page={selectedPage}
        pageSlug={selectedSlug}
        loading={pageQuery.isLoading}
        error={pageQuery.isError ? pageQuery.error.message : null}
        draft={draft}
        canManage={canManage}
        blocksError={blocksError}
        operationError={operationError}
        onClose={closeEditor}
        onDraftChange={handleDraftChange}
        onApplyTemplate={(templateKey) => {
          const next = draftFromTemplate(templateKey);
          if (next) {
            setDraft(next);
            setIsDirty(true);
            setBlocksError(null);
          }
        }}
        onSave={() => void handleSaveDraft()}
        onPublish={() => void handlePublish()}
        onUnpublish={() => void handleUnpublish()}
        onDelete={() => void handleDelete()}
        onReset={handleResetDraft}
        saving={updateMutation.isPending || createMutation.isPending || replaceBlocksMutation.isPending}
        publishing={publishMutation.isPending}
        unpublishing={unpublishMutation.isPending}
        deleting={deleteMutation.isPending}
      />

      <ContentSettingsDrawer open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </PageContainer>
  );
}
